/**
 * Radius (px) for tape-imbalance layer dots on the historical chart.
 * Uses midrank among visible days so the smallest/largest tape-total days
 * hit min/max radius even when absolute volumes cluster (common on liquid names).
 */

const RADIUS_MIN_PX = 2.25;
const RADIUS_MAX_PX = 13.5;

/** Total shares counted in the tape imbalance series for one day. */
export function tapeImbalanceTotalVolume(
  buyShares: number,
  sellShares: number,
  neutralShares: number,
): number {
  const n = buyShares + sellShares + neutralShares;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * For each date, radius monotonic in total tape volume among `rows`, with ties
 * sharing the same radius. Spreads sizes across [RADIUS_MIN_PX, RADIUS_MAX_PX].
 */
export function tapeImbalanceDotRadiusByDate(
  rows: Array<{ date: string; totalVolume: number }>,
): Map<string, number> {
  const out = new Map<string, number>();
  const valid = rows.filter((r) => r.totalVolume > 0);
  const n = valid.length;
  if (n === 0) return out;

  const sorted = [...valid].sort((a, b) => {
    const d = a.totalVolume - b.totalVolume;
    if (d !== 0) return d;
    return a.date.localeCompare(b.date);
  });

  if (n === 1) {
    out.set(sorted[0].date, (RADIUS_MIN_PX + RADIUS_MAX_PX) / 2);
    return out;
  }

  const span = RADIUS_MAX_PX - RADIUS_MIN_PX;
  let i = 0;
  while (i < n) {
    let j = i + 1;
    while (j < n && sorted[j].totalVolume === sorted[i].totalVolume) {
      j += 1;
    }
    const lo = i;
    const hi = j - 1;
    const avgRank = (lo + hi) / 2;
    const t = avgRank / (n - 1);
    const r = RADIUS_MIN_PX + t * span;
    for (let k = i; k < j; k += 1) {
      out.set(sorted[k].date, r);
    }
    i = j;
  }

  return out;
}
